import { API_KEY, ENV } from '@env';
import { PhotosiSdk } from '@photosi/expo-photosi-sdk/build/PhotosiSdk';
import { getUserInfo } from 'src/_shared/utils/auth/getUserInfo';
import { getLanguage } from 'src/_shared/utils/getLanguage';
import { updateProjectOptions } from 'src/_shared/utils/project-options/update-project-options/updateProjectOptions';
import { SDK_REMOTES } from 'src/app/settings';
import { IRootStore, RootStore } from './store';
import { User } from './user/user';
import { isNewEditor } from 'src/_shared/utils/is-new-editor/isNewEditor';
import { SKIPPED_INIT_PROJECTS_PATHS } from 'src/routes/_shared/routes.constants';
import { logInfo } from 'src/_shared/logger';
import { getConnectionInfo } from 'src/_shared/utils/get-connection-info/getConnectionInfo';
import Datadog from 'src/_shared/datadog';
import { DATADOG_CUSTOM_ACTIONS } from 'src/_shared/datadog/DatadogCustomActions';
import { BOOTSTRAP_CONTEXT, captureConnectionContext } from 'src/_shared/datadog/bootstrapContext';
import {
  markBootstrapStep,
  startBootstrapWatchdog,
  stopBootstrapWatchdog,
} from 'src/_shared/datadog/bootstrap-steps/bootstrapSteps';

export async function initStore() {
  startBootstrapWatchdog();
  markBootstrapStep('user_info');
  const { accessToken, id, isLogged, userType } = await getUserInfo();

  const start = performance.now();
  Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_sdk_init_time);
  markBootstrapStep('sdk_init');
  const sdk = await PhotosiSdk.init({
    apiKey: API_KEY,
    env: ENV,
    userAgent: 'MIOFOTOGRAFO/10.0.0 (Software/MIOFOTOGRAFO; +http://corporate.photosi.com/)',
    jwtToken: accessToken,
    language: getLanguage(),
    sdkRemotes: SDK_REMOTES,
  });
  Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_sdk_init_time, { ...BOOTSTRAP_CONTEXT, ...captureConnectionContext() });
  const end = performance.now();
  const sdkInitializationTime: number = (end - start) / 1000;

  const deviceInfo = {
    sdkInitTime: sdkInitializationTime.toFixed(2) + 's',
    sdkInitConnectionType:
      sdkInitializationTime < 0.7 ? '5G' : sdkInitializationTime < 5 ? '4G' : sdkInitializationTime < 20 ? '3G' : '2G',
    connection: getConnectionInfo(),
  };

  logInfo('[initStore] COMPLETED SDK initialization', deviceInfo);

  const store: IRootStore = RootStore.create(
    {
      isStoreReady: true,
      user: User.create({
        id,
        accessToken,
        isLogged,
        userType,
      }),
    },
    { sdk, deviceInfo },
  );
  markBootstrapStep('store_created');

  const skipInitProject = SKIPPED_INIT_PROJECTS_PATHS.some(route => window.location.href.includes(route));

  if (!skipInitProject) {
    Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_project_init_time);
    await store.project.initProject(isLogged, isNewEditor()).then(newProjectOptions => {
      updateProjectOptions(newProjectOptions);
      store.data.setLoading(false);
    });
    Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_project_init_time, { ...BOOTSTRAP_CONTEXT, ...captureConnectionContext() });
  } else {
    store.data.setLoading(false);
  }

  stopBootstrapWatchdog();

  return store;
}
